import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ticketApi } from '../../api/ticket.api';
import { BASE_URL } from '../../api/client';
import { useUI } from '../../store/UIContext';
import { useAuth } from '../../store/AuthContext';
import { DraftTextarea, PageHeader, Btn, ConfirmDialog } from '../../components/ui/index';
import { EmojiBar } from '../../components/ui/EmojiPicker';
import FileDropZone from '../../components/ui/FileDropZone';
import { clearNewTicketDrafts } from '../../utils/draftStorage';
import { validateFile, getAcceptAttribute, getLimitsHint } from '../../utils/fileValidation';
import { resolveTicketTitle } from '../../utils/ticketTitle';
import { ThumbUpIcon, ThumbDownIcon } from '../../components/feedback/FeedbackMoodPicker';

const DEFAULT_TICKET_CATEGORY = 'سایر';

// Office hours are 8:00-17:00; outside that window an "always available"
// notice about the 24/7 AI assistant is shown on the submit page.
const isOutsideOfficeHours = () => {
  const hour = new Date().getHours();
  return hour < 8 || hour >= 17;
};

export default function NewTicketPage() {
  const navigate = useNavigate();
  const { addToast } = useUI();
  const { user } = useAuth() || {};
  const [text, setText] = useState('');
  const [clearBodySignal, setClearBodySignal] = useState(0);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitOverlay, setSubmitOverlay] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [chatMessages, setChatMessages] = useState([]); // [{role:'user'|'assistant', content}]
  const [chatInput, setChatInput] = useState('');
  const [helperFileUrl, setHelperFileUrl] = useState(null);
  const [chatId, setChatId] = useState(null);
  const [feedbackGiven, setFeedbackGiven] = useState(false);
  const [feedbackLoading, setFeedbackLoading] = useState(false);
  const [confirmGoodOpen, setConfirmGoodOpen] = useState(false);
  const [noContract, setNoContract] = useState(false);
  const [noContractDialog, setNoContractDialog] = useState(false);
  useEffect(() => {
    ticketApi.getMyContractStatus()
      .then((res) => setNoContract(res.data?.has_active_contract === false))
      .catch(() => {});
  }, []);
  const NO_CONTRACT_MSG = 'شما کاربر مهمان هستید یا قرارداد فعال ندارید و تیکت شما به دیسپچر ارسال می‌شود تا در زمان مناسب به کارشناس مربوطه ارسال شود. در صورت تمدید قرارداد، تیکت شما بلافاصله به کارشناس مربوطه ارجاع می‌شود.';
  const esRef = useRef(null);
  const chatEndRef = useRef(null);
  const chatInputRef = useRef(null);

  useEffect(() => () => esRef.current?.close(), []);

  // Each visit to "new ticket" starts with a clean AI assistant session.
  useEffect(() => {
    setChatOpen(false);
    setChatMessages([]);
    setChatInput('');
    setChatId(null);
    setFeedbackGiven(false);
    setHelperFileUrl(null);
  }, []);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages, chatOpen]);

  // Files uploaded via handleFileAdd are already persisted server-side
  // (send_file -> tickets_draft_files, see querySaveFileDraft) the moment
  // they're picked - but this page's `files` state was never populated
  // back from that on mount, so a page reload made already-uploaded
  // attachments silently disappear from the UI even though they were still
  // staged and would still show up once the ticket is sent.
  useEffect(() => {
    ticketApi.getTicketDraft(0)
      .then((res) => {
        const list = Array.isArray(res.data?.files_list) ? res.data.files_list : [];
        setFiles(list.map((f) => ({ name: f.main_file_name, serverName: f.server_file_name, size: f.size })));
      })
      .catch(() => {});
  }, []);

  const handleFileAdd = async (f) => {
    if (!f) return;
    const validationError = validateFile(f, user?.type, files);
    if (validationError) { addToast(validationError, 'error'); return; }
    // Backend expects JSON {file_name, url (base64 data-URL), main_ticket_id}
    const dataUrl = await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (ev) => resolve(ev.target.result);
      reader.readAsDataURL(f);
    });
    try {
      const res = await ticketApi.sendFile({ file_name: f.name, url: dataUrl, main_ticket_id: 0 });
      const serverName = res.data?.file_name || f.name;
      setFiles((prev) => [...prev, { name: f.name, serverName, size: f.size }]);
      addToast('فایل آپلود شد', 'success');
    } catch { addToast('خطا در آپلود فایل', 'error'); }
  };

  const removeFile = async (serverName, originalName) => {
    // Backend expects main_ticket_id + main_file_name (original client-side name)
    try { await ticketApi.deleteFileFromDraft({ main_ticket_id: 0, main_file_name: originalName }); } catch {}
    setFiles((prev) => prev.filter((f) => f.serverName !== serverName));
  };

  const resetAfterSuccessfulSend = async () => {
    await clearNewTicketDrafts(user?.username);
    setText('');
    setClearBodySignal((c) => c + 1);
    setFiles([]);
    setChatOpen(false);
    setChatMessages([]);
    setChatInput('');
    setChatId(null);
    setFeedbackGiven(false);
    setHelperFileUrl(null);
    esRef.current?.close();
  };

  // Sends the full chat history (stateless backend) and streams the
  // assistant's reply into the last (placeholder) chat message.
  const sendToAI = (history, currentChatId) => {
    setAiLoading(true);
    setHelperFileUrl(null);
    setFeedbackGiven(false);
    setChatMessages((prev) => [...prev, { role: 'assistant', content: '' }]);

    // POST (not EventSource/GET) so a long chat history rides in the body
    // instead of the URL - a long ticket description or multi-turn chat
    // pushed the GET query string past the proxy's URL-length limit.
    const controller = new AbortController();
    esRef.current = { close: () => controller.abort() };
    let assistantText = '';
    let done = false;

    const handleEvent = (raw) => {
      if (raw === '[DONE]') {
        done = true;
        setAiLoading(false);
        return;
      }
      try {
        const event = JSON.parse(raw);
        if (event.type === 'chat_id') {
          setChatId(event.chat_id);
        } else if (event.type === 'chunk') {
          assistantText += event.content;
          setChatMessages((prev) => {
            const next = [...prev];
            next[next.length - 1] = { role: 'assistant', content: assistantText };
            return next;
          });
        } else if (event.type === 'file') {
          setHelperFileUrl(event.helper_bat_file_address);
        } else if (event.type === 'error') {
          addToast('خطا در دریافت پاسخ دستیار هوش‌مصنوعی', 'error');
        }
      } catch {}
    };

    fetch(`${BASE_URL}ask_ai_assistant`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ history, chat_id: currentChatId || undefined }),
      signal: controller.signal,
    })
      .then(async (res) => {
        if (!res.ok || !res.body) throw new Error(`HTTP ${res.status}`);
        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        for (;;) {
          const { value, done: streamDone } = await reader.read();
          if (streamDone) break;
          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n\n');
          buffer = lines.pop();
          for (const line of lines) {
            if (line.startsWith('data: ')) handleEvent(line.slice(6).trim());
          }
        }
        if (!done) setAiLoading(false);
      })
      .catch(() => {
        if (done) return;
        addToast('خطا در ارتباط با دستیار هوش‌مصنوعی', 'error');
        setAiLoading(false);
      });
  };

  const askAI = () => {
    if (!text.trim()) { addToast('برای کمک هوش‌مصنوعی، متن تیکت را وارد کنید', 'error'); return; }
    setChatOpen(true);
    setChatId(null);
    const firstMessage = { role: 'user', content: text };
    const history = [firstMessage];
    setChatMessages(history);
    sendToAI(history, null);
  };

  const sendChatMessage = () => {
    if (!chatInput.trim() || aiLoading) return;
    const history = [...chatMessages, { role: 'user', content: chatInput.trim() }];
    setChatMessages(history);
    setChatInput('');
    sendToAI(history, chatId);
  };

  const sendFeedback = async (value) => {
    if (!chatId || feedbackLoading) return;
    setFeedbackLoading(true);
    setSubmitOverlay(true);
    try {
      const generatedTitle = await resolveTicketTitle(text);
      const res = await ticketApi.aiChatFeedback({
        chat_id: chatId, feedback: value, title: generatedTitle, description: text, category: DEFAULT_TICKET_CATEGORY, priority: 0,
      });
      setFeedbackGiven(true);
      if (value === 'good' && res.data?.ticket_id) {
        addToast('تیکت شما با پاسخ هوش‌مصنوعی به صورت خودکار ثبت شد', 'success');
        await resetAfterSuccessfulSend();
        navigate('/dashboard');
      } else {
        addToast('بازخورد شما ثبت شد', 'success');
      }
    } catch {
      addToast('خطا در ثبت بازخورد', 'error');
    }
    setFeedbackLoading(false);
    setSubmitOverlay(false);
  };

  const submit = async (skipNotice = false) => {
    if (!text.trim()) { addToast('متن تیکت الزامی است', 'error'); return; }
    if (noContract && skipNotice !== true) { setNoContractDialog(true); return; }
    setLoading(true);
    setSubmitOverlay(true);
    try {
      const generatedTitle = await resolveTicketTitle(text);
      // Backend params: title, description, category, main_ticket_id, priority
      // priority is an integer column in MySQL (default 0, see tickets_draft default in QueryFunctions.py) — not a string label
      // chat_id links the preceding AI assistant conversation (if any) to
      // this human ticket so managers can review that process later.
      await ticketApi.sendTicket({
        title: generatedTitle,
        description: text,
        category: DEFAULT_TICKET_CATEGORY,
        main_ticket_id: 0,
        priority: 0,
        ...(chatId ? { chat_id: chatId } : {}),
      });
      addToast('تیکت با موفقیت ارسال شد', 'success');
      await resetAfterSuccessfulSend();
      navigate('/dashboard');
    } catch (err) {
      const status = err.response?.status;
      const body = err.response?.data;
      if (status === 304) addToast('شماره موبایل یا ایمیل تأیید نشده — پروفایل را تکمیل کنید', 'error');
      else if (status === 403) addToast('دسترسی ندارید یا جلسه منقضی شده', 'error');
      else if (status === 501) addToast('خطا در سرور', 'error');
      else addToast(typeof body === 'string' && body ? body : 'خطا در ارسال تیکت', 'error');
    }
    setLoading(false);
    setSubmitOverlay(false);
  };

  return (
    <div className="fade-in">
      <PageHeader title="ارسال تیکت جدید" subtitle="مشکل یا درخواست خود را شرح دهید" />
      <ConfirmDialog
        open={noContractDialog}
        onClose={() => setNoContractDialog(false)}
        onConfirm={() => submit(true)}
        title="اطلاعیه"
        message={NO_CONTRACT_MSG}
      />

      <div style={{ maxWidth: 720, background: '#fff', borderRadius: 16, border: '1px solid rgba(59,130,246,0.1)', boxShadow: '0 4px 16px rgba(59,130,246,0.08)', padding: 32 }}>

        {noContract && (
          <div style={{ marginBottom: 20, borderRadius: 10, background: '#fffbeb', border: '1px solid #fde68a', padding: '12px 16px', fontSize: 12.5, color: '#92400e', lineHeight: 1.9 }}>
            {NO_CONTRACT_MSG}
          </div>
        )}

        {isOutsideOfficeHours() && (
          <div style={{ marginBottom: 20, borderRadius: 10, background: '#f8faff', border: '1px solid #bfdbfe', padding: '12px 16px' }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#2563eb', marginBottom: 4 }}>
              پشتیبانی همیشه همراه شماست
            </div>
            <div style={{ fontSize: 12.5, color: '#334155', lineHeight: 1.9 }}>
              دستیار هوشمند پژواک به‌صورت ۲۴ ساعته آماده پاسخ‌گویی و راهنمایی شماست. کارشناسان پشتیبانی نیز در ساعات اداری درخواست‌های شما را بررسی و پاسخ می‌دهند.
            </div>
          </div>
        )}

        {/* Text - with auto-draft */}
        <div style={{ marginBottom: 20 }}>
          <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#334155', marginBottom: 8 }}>شرح کامل <span style={{ color: '#ef4444' }}>*</span></label>
          <DraftTextarea draftKey="new_ticket_text" rows={6} placeholder="مشکل خود را با جزئیات کامل شرح دهید..." onChange={(v) => setText(v)} clearTrigger={clearBodySignal} onSubmit={() => { if (!loading && !submitOverlay) submit(); }} />
        </div>

        {/* AI Assistant */}
        <div style={{ marginBottom: 24 }}>
          {!chatOpen && (
            <Btn variant="secondary" onClick={askAI} loading={aiLoading}>
              🤖 کمک از دستیار هوش‌مصنوعی
            </Btn>
          )}
          {chatOpen && (
            <div style={{ borderRadius: 10, background: '#f8faff', border: '1px solid #bfdbfe', overflow: 'hidden' }}>
              <div style={{ padding: '10px 16px', borderBottom: '1px solid #bfdbfe', fontSize: 12, fontWeight: 600, color: '#2563eb' }}>
                🤖 گفتگو با دستیار هوش‌مصنوعی
              </div>
              <div style={{ maxHeight: 320, overflowY: 'auto', padding: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
                {chatMessages.map((m, i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: m.role === 'user' ? 'flex-start' : 'flex-end' }}>
                    <div style={{
                      maxWidth: '85%', padding: '8px 12px', borderRadius: 10, fontSize: 13, whiteSpace: 'pre-wrap',
                      background: m.role === 'user' ? '#eff6ff' : '#fff',
                      color: '#334155',
                      border: m.role === 'user' ? '1px solid #bfdbfe' : '1px solid #e2e8f0',
                    }}>
                      {m.content || (aiLoading && i === chatMessages.length - 1 ? '...' : '')}
                    </div>
                  </div>
                ))}
                {helperFileUrl && (
                  <a href={helperFileUrl} download
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 600, color: '#2563eb' }}>
                    📥 دانلود فایل راهنما
                  </a>
                )}
                {!aiLoading && chatId && !feedbackGiven && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 12, color: '#64748b' }}>این پاسخ به شما کمک کرد؟</span>
                    <button type="button" onClick={() => setConfirmGoodOpen(true)} disabled={feedbackLoading}
                      className="pj-fb-thumb pj-fb-thumb--up" aria-label="بله، مفید بود">
                      <ThumbUpIcon />
                      بله
                    </button>
                    <button type="button" onClick={() => sendFeedback('bad')} disabled={feedbackLoading}
                      className="pj-fb-thumb pj-fb-thumb--down" aria-label="خیر، مفید نبود">
                      <ThumbDownIcon />
                      خیر
                    </button>
                    <ConfirmDialog
                      open={confirmGoodOpen}
                      onClose={() => setConfirmGoodOpen(false)}
                      onConfirm={() => sendFeedback('good')}
                      title="تایید رضایت از پاسخ هوش‌مصنوعی"
                      message="تیکت شما برای کارشناسان پژواک ارسال نخواهد شد. آیا تایید می‌کنید؟"
                    />
                  </div>
                )}
                <div ref={chatEndRef} />
              </div>
              <div style={{ display: 'flex', gap: 8, padding: 12, borderTop: '1px solid #bfdbfe', alignItems: 'center' }}>
                <EmojiBar inputRef={chatInputRef} value={chatInput} onInsert={setChatInput} />
                <input
                  ref={chatInputRef}
                  value={chatInput}
                  onChange={(e) => setChatInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendChatMessage(); } }}
                  placeholder="پیام خود را بنویسید..."
                  disabled={aiLoading}
                  style={{ flex: 1, padding: '9px 12px', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 13, outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box' }}
                />
                <Btn variant="secondary" onClick={sendChatMessage} loading={aiLoading} disabled={!chatInput.trim()}>
                  ارسال
                </Btn>
              </div>
            </div>
          )}
        </div>

        {/* File attachment */}
        <div style={{ marginBottom: 24 }}>
          <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#334155', marginBottom: 8 }}>پیوست‌ها</label>
          <FileDropZone
            onFile={handleFileAdd}
            accept={getAcceptAttribute(user?.type)}
            limitsHint={getLimitsHint(user?.type)}
          />
          {files.length > 0 && (
            <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 6 }}>
              {files.map((f) => (
                <div key={f.serverName} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', background: '#f8faff', borderRadius: 8, border: '1px solid #e2e8f0' }}>
                  <span style={{ fontSize: 16 }}>📄</span>
                  <span style={{ flex: 1, fontSize: 12, color: '#334155', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.name}</span>
                  <button onClick={() => removeFile(f.serverName, f.name)} style={{ color: '#ef4444', background: 'none', border: 'none', cursor: 'pointer', fontSize: 14 }}>✕</button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Actions */}
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <Btn variant="ghost" onClick={() => navigate(-1)}>انصراف</Btn>
          <Btn onClick={() => submit()} loading={loading} disabled={submitOverlay}>
            📤 ارسال تیکت
          </Btn>
        </div>
      </div>
    </div>
  );
}
